/**
 * Utility functions for working with enum objects
 */

/**
 * Get label from an enum object by trying multiple possible field names
 * @param enumObject - The enum object (skill, interest, event type, etc.)
 * @param possibleFields - Array of possible field names to check (in order of priority)
 * @param fallback - Fallback value if no field is found
 * @returns The label string or fallback value
 */
export const getEnumLabel = (
  enumObject: any,
  possibleFields: string[],
  fallback: string = ''
): string => {
  if (!enumObject) return fallback;

  for (const field of possibleFields) {
    if (enumObject[field]) {
      return enumObject[field];
    }
  }

  return fallback;
};

/**
 * Get skill label from skill data object
 * Tries: skill_name, category_name, name
 */
export const getSkillLabel = (skillData: any, fallback: string = ''): string => {
  return getEnumLabel(skillData, ['skill_name', 'category_name', 'name'], fallback);
};

/**
 * Get interest label from interest data object
 * Tries: interest_name, category_name, name
 */
export const getInterestLabel = (interestData: any, fallback: string = ''): string => {
  return getEnumLabel(interestData, ['interest_name', 'category_name', 'name'], fallback);
};

/**
 * Get event type label from event type data object
 * Tries: type_name, event_type_name, name
 */
export const getEventTypeLabel = (eventTypeData: any, fallback: string = ''): string => {
  return getEnumLabel(eventTypeData, ['type_name', 'event_type_name', 'name'], fallback);
};

/**
 * Get nested skill item name based on user type
 * Tries: skill_name_vol (for volunteer), skill_name_org (for organisation), skill_name, name
 */
export const getNestedSkillName = (skillItem: any, userType: 'volunteer' | 'organisation' = 'volunteer'): string => {
  const fields = userType === 'volunteer' 
    ? ['skill_name_vol', 'skill_name', 'name']
    : ['skill_name_org', 'skill_name', 'name'];
  return getEnumLabel(skillItem, fields);
};

/**
 * Get nested interest item name based on user type
 * Tries: interest_name_vol (for volunteer), interest_name_org (for organisation), interest_name, name
 */
export const getNestedInterestName = (interestItem: any, userType: 'volunteer' | 'organisation' = 'volunteer'): string => {
  const fields = userType === 'volunteer' 
    ? ['interest_name_vol', 'interest_name', 'name']
    : ['interest_name_org', 'interest_name', 'name'];
  return getEnumLabel(interestItem, fields);
};
